import { gql, useApolloClient } from "@apollo/client";
import useNotifier from "@dashboard/hooks/useNotifier";
import { useState } from "react";
import { useIntl } from "react-intl";

import { cegidInvoicePdfMessages as messages } from "./messages";

export const cegidInvoicePdfQuery = gql`
  query CegidInvoicePdf($invoiceId: ID!) {
    cegidInvoicePdf(invoiceId: $invoiceId) {
      fileName
      contentBase64
    }
  }
`;

interface CegidInvoicePdfQueryData {
  cegidInvoicePdf: { fileName: string; contentBase64: string } | null;
}

export type CegidInvoicePdfAction = "print" | "download" | "open";

export const base64ToPdfBlob = (contentBase64: string): Blob => {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: "application/pdf" });
};

// Blob URLs stay valid while the print dialog is open; free them afterwards.
const REVOKE_AFTER_MS = 60_000;

const printPdf = (url: string) => {
  const frame = document.createElement("iframe");

  Object.assign(frame.style, {
    position: "fixed",
    right: "0",
    bottom: "0",
    width: "0",
    height: "0",
    border: "0",
  });
  frame.src = url;
  frame.onload = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    setTimeout(() => frame.remove(), REVOKE_AFTER_MS);
  };
  document.body.appendChild(frame);
};

const downloadPdf = (url: string, fileName: string) => {
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Fetches a CEGID invoice's archived PDF through the authenticated API (the
 * file is never public) and prints, downloads or opens it.
 */
export const useCegidInvoicePdf = () => {
  const client = useApolloClient();
  const notify = useNotifier();
  const intl = useIntl();
  const [pendingInvoiceId, setPendingInvoiceId] = useState<string | null>(null);

  const run = async (invoiceId: string, action: CegidInvoicePdfAction) => {
    // Opened synchronously so the browser doesn't treat it as a popup.
    const tab = action === "open" ? window.open("", "_blank") : null;

    setPendingInvoiceId(invoiceId);

    try {
      const { data } = await client.query<CegidInvoicePdfQueryData, { invoiceId: string }>({
        query: cegidInvoicePdfQuery,
        variables: { invoiceId },
        // Each PDF is fetched on demand; don't keep base64 blobs in the cache.
        fetchPolicy: "no-cache",
      });
      const pdf = data?.cegidInvoicePdf;

      if (!pdf) {
        throw new Error(intl.formatMessage(messages.pdfUnavailable));
      }

      const url = URL.createObjectURL(base64ToPdfBlob(pdf.contentBase64));

      if (action === "print") {
        printPdf(url);
      } else if (action === "download") {
        downloadPdf(url, pdf.fileName);
      } else if (tab) {
        tab.location.href = url;
      }

      setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
    } catch (error) {
      tab?.close();
      notify({
        status: "error",
        title: intl.formatMessage(messages.pdfError),
        text: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setPendingInvoiceId(null);
    }
  };

  return { run, pendingInvoiceId };
};
